import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

// components
import { Slider } from 'primereact/slider'
import { InputSwitch } from 'primereact/inputswitch'

import ToggleTriplet from '@aytm/app/stats/components/toggle-triplet/ToggleTriplet'

// styles
import style from '@aytm/app/stats/page/question-views/components/controls/styles/Controls.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const parseMode = (mode = 'pba') => {
  return {
    first: mode.charAt(0) || 'p', // 'd' | 'p'
    second: mode.charAt(1) || 'b', // 'b' | 'd'
    stat: mode.charAt(2) || 'a' // 'a' | 'm'
  }
}

const calcMode = ({ first, second, stat }) => ('d' === first ? 'd' : `p${second}${stat}`)

const withSecond = (mode, second) => calcMode({ ...parseMode(mode), second })

// Space on a switch clicks its hidden input, whose change flips the switch; the same click then
// reaches the switch's own click handler, which reports the flip a second time
const isInputClickEcho = event =>
  'click' === event.originalEvent?.type && 'INPUT' === event.originalEvent.target?.tagName

const Controls = ({ neutral = 0, mode = 'pba', degree = 201, onModelChange }) => {
  const [neutralLocal, setNeutralLocal] = useState(neutral)

  const { first, second, stat } = useMemo(() => parseMode(mode), [mode])

  // Remember the last intensity family/metric so re-entering Intensity from Popularity
  // restores the prior choice (e.g. Deltas/Median) instead of resetting to Pairs/Mean.
  const lastIntensity = useRef({ second: 'b', stat: 'a' })
  useEffect(() => {
    if ('p' === first) lastIntensity.current = { second, stat }
  }, [first, second, stat])

  // Neutral threshold ceiling: one less than the integer half of the scale degree
  // (e.g. 7-point scale → 2). When degree is absent the renderer's 201 sentinel yields 99.
  const neutralMax = useMemo(() => Math.max(0, Math.floor(degree / 2) - 1), [degree])

  const commitNeutral = value => {
    if (value !== neutral) {
      onModelChange?.({ neutral: value })
    }
  }
  // a drag commits once, when it ends; an arrow key is a whole move of its own, and the slider
  // reports it through onChange only -- it never fires onSlideEnd for the keyboard
  const onSlideChange = event => {
    setNeutralLocal(event.value)
    if ('keydown' === event.originalEvent?.type) commitNeutral(event.value)
  }
  const onSlideEnd = event => commitNeutral(event.value)

  const setFirst = useCallback(
    v => {
      const next =
        'd' === v
          ? 'd'
          : calcMode({ first: 'p', second: lastIntensity.current.second, stat: lastIntensity.current.stat })
      if (next !== mode) onModelChange?.({ mode: next })
    },
    [mode, onModelChange]
  )

  const setSecond = useCallback(
    v => {
      const next = withSecond(mode, v)

      if (next !== mode) {
        onModelChange?.({ mode: next })
      }
    },
    [mode, onModelChange]
  )

  useEffect(() => setNeutralLocal(neutral), [neutral])

  // that handler also cancels the click, and a cancelled click puts the input's box back after the
  // render has set it; a timer runs only once the click is over, so it sets the box again
  const intensityInput = useRef(null)
  const deltasInput = useRef(null)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (intensityInput.current) intensityInput.current.checked = 'p' == first
      if (deltasInput.current) deltasInput.current.checked = 'd' == second
    })
    return () => clearTimeout(timer)
  }, [first, second])

  return (
    <div className={`${style.component} ${NO_PRINT}`}>
      <ToggleTriplet
        name="psc-family"
        leftLabel="Popularity"
        rightLabel="Intensity"
        activeLeft={'d' == first}
        onLeftClick={() => setFirst('d')}
        onRightClick={() => setFirst('p')}
      >
        <InputSwitch
          aria-label="Intensity"
          checked={'p' == first}
          inputRef={intensityInput}
          onChange={event => isInputClickEcho(event) || setFirst(event.value ? 'p' : 'd')}
        />
      </ToggleTriplet>

      {'p' == first ? (
        <ToggleTriplet
          name="psc-pairing"
          leftLabel="Pairs"
          rightLabel="Deltas"
          activeLeft={'b' == second}
          onLeftClick={() => setSecond('b')}
          onRightClick={() => setSecond('d')}
        >
          <InputSwitch
            aria-label="Deltas"
            checked={'d' == second}
            inputRef={deltasInput}
            onChange={event => isInputClickEcho(event) || setSecond(event.value ? 'd' : 'b')}
          />
        </ToggleTriplet>
      ) : null}

      {'d' == first ? (
        <>
          <div className={`${style.slider} p-fluid p-col-4`}>
            <Slider
              aria-label="Count as Neutral"
              min={0}
              max={neutralMax}
              value={neutralLocal}
              labelTemplate={value => `±${value}`}
              onChange={onSlideChange}
              onSlideEnd={onSlideEnd}
            />
          </div>
          <div className="p-col">
            <span className={style['slider-title']}>
              Count as
              <br />
              Neutral
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}

Controls.displayName = 'Controls'

Controls.propTypes = {
  neutral: PropTypes.number,
  mode: PropTypes.string,
  degree: PropTypes.number,
  onModelChange: PropTypes.func
}

export default Controls
